import { create } from 'zustand';
import { api } from '../services/api';
import type { User, ApiResponse, AuthTokens } from '@dongtan/shared';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, phone?: string) => Promise<void>;
  socialLogin: (provider: string, code: string, redirectUri: string, state?: string) => Promise<void>;
  logout: () => void;
  fetchProfile: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  isAuthenticated: !!localStorage.getItem('accessToken'),

  login: async (email, password) => {
    set({ isLoading: true });
    try {
      const { data } = await api.post<ApiResponse<{ user: User } & AuthTokens>>('/auth/login', {
        email,
        password,
      });
      if (data.success) {
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        set({ user: data.data.user, isAuthenticated: true });
      }
    } finally {
      set({ isLoading: false });
    }
  },

  register: async (email, password, name, phone) => {
    set({ isLoading: true });
    try {
      const { data } = await api.post<ApiResponse<{ user: User } & AuthTokens>>(
        '/auth/register',
        { email, password, name, phone },
      );
      if (data.success) {
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        set({ user: data.data.user, isAuthenticated: true });
      }
    } finally {
      set({ isLoading: false });
    }
  },

  socialLogin: async (provider, code, redirectUri, state) => {
    set({ isLoading: true });
    try {
      const { data } = await api.post<ApiResponse<{ user: User } & AuthTokens>>(
        `/auth/social/${provider}`,
        { code, redirectUri, state },
      );
      if (data.success) {
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        set({ user: data.data.user, isAuthenticated: true });
      }
    } finally {
      set({ isLoading: false });
    }
  },

  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    set({ user: null, isAuthenticated: false });
  },

  fetchProfile: async () => {
    try {
      const { data } = await api.get<ApiResponse<User>>('/auth/me');
      if (data.success) {
        set({ user: data.data, isAuthenticated: true });
      }
    } catch {
      set({ user: null, isAuthenticated: false });
    }
  },
}));
